import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../src/contexts/AuthContext';
import { gsap } from 'gsap';
import Swal from 'sweetalert2';

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFormData({
      phone: '',
      password: '',
    });

    // GSAP Entrance Animations
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.8 } });

      tl.fromTo(cardRef.current,
        { y: 30, opacity: 0, scale: 0.98 },
        { y: 0, opacity: 1, scale: 1, delay: 0.2 }
      )
        .fromTo(titleRef.current?.children || [],
          { y: 20, opacity: 0 },
          { y: 0, opacity: 1, stagger: 0.1 },
          "-=0.4"
        )
        .fromTo(formRef.current?.children || [],
          { y: 15, opacity: 0 },
          { y: 0, opacity: 1, stagger: 0.1 },
          "-=0.5"
        );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const isDarkMode = () => {
    const theme = localStorage.getItem('theme');
    if (theme) return theme === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const phone = formData.phone.trim();
    if (!phone || !formData.password) {
      setError('Por favor ingresa tu teléfono y contraseña');
      return;
    }
    if (!/^\d{10}$/.test(phone)) {
      setError('El teléfono debe tener exactamente 10 dígitos');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      await login(phone, formData.password);
      const dark = isDarkMode();
      Swal.fire({
        toast: true,
        icon: 'success',
        title: 'Sesión iniciada',
        position: 'top-end',
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
        background: dark ? '#1a1614' : '#ffffff',
        color: dark ? '#ffffff' : '#1a1614',
        customClass: {
          popup: 'rounded-2xl'
        }
      });
      navigate('/');
    } catch (err) {
      const dark = isDarkMode();
      Swal.fire({
        icon: 'error',
        title: 'Credenciales incorrectas',
        text: 'El teléfono o la contraseña son incorrectos',
        confirmButtonColor: '#f97316',
        background: dark ? '#1a1614' : '#ffffff',
        color: dark ? '#ffffff' : '#1a1614',
        customClass: {
          popup: 'rounded-2xl'
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen relative overflow-hidden bg-background-light dark:bg-background-dark flex items-center justify-center p-6">
      {/* Background Abstract Shapes */}
      <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] bg-orange-500/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10 dark:bg-slate-800/50 dark:rounded-[2.5rem] bg-gray-200 rounded-[2.5rem] shadow-xl">
        <div ref={cardRef} className="glass-card rounded-[2.5rem] p-8 md:p-10">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="absolute top-6 left-6 w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
          >
            <span className="material-symbols-outlined text-gray-600 dark:text-gray-300">arrow_back</span>
          </button>
          <div ref={titleRef} className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-primary to-orange-600 shadow-lg shadow-primary/20 mb-6 group transition-transform duration-500 hover:rotate-6">
              <span className="material-symbols-outlined text-white text-4xl">restaurant</span>
            </div>
            <h1 className="text-3xl font-black text-secondary dark:text-white tracking-tight mb-2">Bienvenido</h1>
            <p className="text-gray-500 dark:text-gray-400 font-medium">Inicia sesión para disfrutar</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 flex items-center gap-3 animate-shake">
              <span className="material-symbols-outlined text-red-500">error</span>
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">{error}</p>
            </div>
          )}

          <form ref={formRef} className="space-y-5 max-w-sm mx-auto" onSubmit={handleSubmit}>
            <div className="space-y-2 text-center">
              <label className="text-sm font-bold text-secondary dark:text-gray-300">Número de teléfono</label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 group-focus-within:text-primary transition-colors">phone</span>
                <input
                  name="phone"
                  type="tel"
                  required
                  maxLength={10}
                  pattern="\d{10}"
                  inputMode="numeric"
                  value={formData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData(prev => ({ ...prev, phone: val }));
                  }}
                  className="input-premium pl-12 rounded-full text-gray-700 dark:text-gray-700"
                  placeholder="10 dígitos"
                />
              </div>
            </div>

            <div className="space-y-2 text-center">
              <div className="flex flex-col gap-1 items-center">
                <label className="text-sm font-bold text-secondary dark:text-gray-300">Contraseña</label>
                <button type="button" className="text-xs font-bold text-primary hover:underline">¿La olvidaste?</button>
              </div>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 group-focus-within:text-primary transition-colors">lock</span>
                <input
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input-premium pl-12 rounded-full text-gray-700 dark:text-gray-700"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="pt-4 space-y-4 mx-auto max-w-xs">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary-premium w-full group overflow-hidden relative"
              >
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 pointer-events-none"></div>
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>
                    <span className="font-bold text-secondary text-gray-500 dark:text-gray-400">Entrar</span>
                    <span className="material-symbols-outlined text-xl group-hover:translate-x-1 transition-transform ">arrow_forward</span>
                  </>
                )}
              </button>

            </div>
          </form>

          {/* <p className="mt-10 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-primary font-bold hover:underline">Crea una ahora</Link>
          </p> */}
        </div>
      </div>
    </div>
  );
};

export default Login;
